import { toast } from "@ui/molecules/Toasts/toast";
import type { TextColor } from "@ui/typography/Text";
import { type Effort, listQuestionEfforts, updateSettings } from "@/client";

export type QuestionEffort = Effort["id"];

/** Langchain describes efforts in words only; how they look is up to us. */
export const QUESTION_EFFORT_SYMBOLS: Record<
	QuestionEffort,
	{ symbol: string; color: TextColor }
> = {
	auto: { symbol: "auto_awesome", color: "primary" },
	none: { symbol: "signal_cellular_0_bar", color: "primary" },
	low: { symbol: "signal_cellular_1_bar", color: "primary" },
	medium: { symbol: "signal_cellular_2_bar", color: "primary" },
	high: { symbol: "signal_cellular_3_bar", color: "primary" },
	max: { symbol: "signal_cellular_4_bar", color: "primary" },
};

declare const pageVars:
	| { defaultEffort?: string; max_question_effort?: string | null }
	| undefined;

export const isQuestionEffort = (value: unknown): value is QuestionEffort =>
	typeof value === "string" && Object.hasOwn(QUESTION_EFFORT_SYMBOLS, value);

/**
 * Efforts from least to most research, as MaxQuestionEffort orders them on
 * the server. Auto isn't on this scale, and is open to everyone: it picks a
 * depth to suit each question.
 */
const EFFORT_DEPTH: QuestionEffort[] = ["none", "low", "medium", "high", "max"];

/**
 * The deepest effort this visitor may ask at, or null when every effort is
 * open to them. Only guests are limited.
 */
export const maxQuestionEffort = (): QuestionEffort | null => {
	const max =
		typeof pageVars === "undefined" ? undefined : pageVars?.max_question_effort;
	return isQuestionEffort(max) ? max : null;
};

export const isQuestionEffortAllowed = (effort: QuestionEffort): boolean => {
	const max = maxQuestionEffort();
	if (max === null || effort === "auto") {
		return true;
	}
	return EFFORT_DEPTH.indexOf(effort) <= EFFORT_DEPTH.indexOf(max);
};

/**
 * The signed-in user's chat.defaultEffort setting. Only signed-in users are
 * given one, so its absence means a guest, whose default lives in this
 * browser instead.
 */
const settingDefaultEffort =
	typeof pageVars === "undefined" ? undefined : pageVars?.defaultEffort;
const isSignedIn = isQuestionEffort(settingDefaultEffort);

const guestStorageKey = "DefaultQuestionEffort";

/** Storage can be unavailable (blocked, private browsing), so a guest just gets auto. */
const readGuestDefaultEffort = (): QuestionEffort | undefined => {
	try {
		const stored = localStorage.getItem(guestStorageKey);
		return isQuestionEffort(stored) ? stored : undefined;
	} catch {
		return undefined;
	}
};

const writeGuestDefaultEffort = (effort: QuestionEffort): void => {
	try {
		localStorage.setItem(guestStorageKey, effort);
	} catch {}
};

/** A default saved before the limit applied (or was lowered) asks at the limit instead. */
const withinMaxEffort = (effort: QuestionEffort): QuestionEffort =>
	isQuestionEffortAllowed(effort) ? effort : (maxQuestionEffort() ?? effort);

let currentDefaultEffort: QuestionEffort = withinMaxEffort(
	isSignedIn ? settingDefaultEffort : (readGuestDefaultEffort() ?? "auto"),
);

/** The effort a question box starts on. Read when the box mounts. */
export const defaultQuestionEffort = (): QuestionEffort => currentDefaultEffort;

/**
 * Asking at an effort other than the default makes it the new default, so
 * boxes opened later (on this page or after a reload) start on it. Call it
 * when a question is asked, not when the effort is chosen.
 */
export const rememberQuestionEffort = async (
	effort: QuestionEffort,
): Promise<void> => {
	if (effort === currentDefaultEffort || !isQuestionEffortAllowed(effort)) {
		return;
	}
	const previous = currentDefaultEffort;
	currentDefaultEffort = effort;
	if (!isSignedIn) {
		writeGuestDefaultEffort(effort);
		return;
	}

	let failed: boolean;
	try {
		const { error } = await updateSettings({
			body: { settings: { "chat.defaultEffort": effort }, scope: "user" },
			// Asking usually navigates straight to the answer, which would cancel the save.
			keepalive: true,
		});
		failed = Boolean(error);
	} catch {
		failed = true;
	}
	if (!failed) {
		return;
	}
	// A later choice may have been saved while this one was in flight.
	if (currentDefaultEffort === effort) {
		currentDefaultEffort = previous;
	}
	toast.error({
		title: "Couldn't save your default effort",
		description: "Your question still uses the effort you chose.",
	});
};

/**
 * A copy of langchain's EFFORT_DISPLAY (langchain/src/model/effort.py), so
 * the choice can be named before the list has loaded, and still be made if it
 * can't be loaded at all. Any difference shows as the label changing on load.
 */
export const FALLBACK_QUESTION_EFFORTS: Effort[] = [
	{
		id: "auto",
		display_name: "Auto",
		description: "Picks the right depth for each question.",
	},
	{
		id: "none",
		display_name: "None",
		description: "Instant answer, no reasoning",
	},
	{
		id: "low",
		display_name: "Low",
		description: "Quick pass over your library",
	},
	{
		id: "medium",
		display_name: "Medium",
		description: "Balanced depth and speed",
	},
	{
		id: "high",
		display_name: "High",
		description: "Checks more clips before answering",
	},
	{
		id: "max",
		display_name: "Max",
		description: "Most thorough, slowest",
	},
];

let loadingEfforts: Promise<Effort[]> | undefined;

/** Fetched once per page, however many boxes offer a choice of effort. */
export const loadQuestionEfforts = (): Promise<Effort[]> => {
	loadingEfforts ??= listQuestionEfforts()
		.then((result) =>
			result.data?.efforts.length
				? result.data.efforts
				: FALLBACK_QUESTION_EFFORTS,
		)
		.catch(() => FALLBACK_QUESTION_EFFORTS);
	return loadingEfforts;
};
